import { DynamicExportCategory, DynamicExportKey } from '@Constants/dynamicExportConstants';
import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { useDynamicExportOptions } from '@Hooks/Exports';
import { useTableMode } from '@Hooks/useTableModes';
import type { ApiInvoiceSnapshot } from '@Model';
import {
    Badge,
    Button,
    defaultIconSize,
    FloatingToolbar,
    SingleSelect,
    type ToolbarOptions,
    useOnTheFlyModal,
} from '@synqo/frontend-component-library';
import { IconDownload, IconTable, IconX } from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { FrameListAllInvoiceSnapshots } from './FrameListAllInvoiceSnapshots';
import { FrameListOpenInvoiceSnapshots } from './FrameListOpenInvoiceSnapshots';
import { invoiceSnapshotRowActionsColumn } from './invoiceSnapshotRowActionsColumn';
import { buildSelection, type ExportFormat } from './invoiceSnapshotExport';
import { InvoiceSnapshotExportButton } from './InvoiceSnapshotExportButton';
import { InvoiceSnapshotExportModal } from './InvoiceSnapshotExportModal';
import type { InvoiceSnapshotRowSelector, InvoiceSnapshotsLoaded } from './invoiceSnapshotListProps';
import { createPreviewRowsSource } from './invoiceSnapshotPreviewRows';
import { InvoiceSnapshotQuickPreview } from './InvoiceSnapshotQuickPreview';
import { InvoiceSnapshotTableModes, invoiceSnapshotTableModes } from './invoiceSnapshotTableModes';
import { useInvoiceSnapshotDocument } from './useInvoiceSnapshotDocument';
import { useInvoiceSnapshotExport } from './useInvoiceSnapshotExport';

/**
 * The invoice list in its active view. Holds selection, loaded rows, toolbar and export dialog, so all of them
 * survive a view switch.
 */
// @ai-generated 2026-09-28T14:03:12+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function FrameListInvoiceSnapshot() {
    const tableModeProps = useTableMode<InvoiceSnapshotTableModes>('all', invoiceSnapshotTableModes);
    const hasPermission = useHasPermission();

    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [loadedRows, setLoadedRows] = useState<ApiInvoiceSnapshot[]>([]);
    const [isExportOpen, setIsExportOpen] = useState(false);

    const { exportOptions } = useDynamicExportOptions(
        DynamicExportCategory.InvoiceSnapshots,
        DynamicExportKey.Overview,
    );

    const objectSelector: InvoiceSnapshotRowSelector = useMemo(
        () => ({ locked: false, selectedObjectIds: selectedIds, setSelectedObjectIds: setSelectedIds }),
        [selectedIds],
    );

    const excelDefinitionId = exportOptions[0]?.id;
    const canReadDocuments = hasPermission(Permission.DocumentsDocumentRead);

    const closeExport = useCallback(() => setIsExportOpen(false), []);
    const clearSelection = useCallback(() => setSelectedIds([]), []);
    const finishExport = useCallback(() => {
        setIsExportOpen(false);
        setSelectedIds([]);
    }, []);
    const { startExport, isStarting, isWaiting, waitingFormat } = useInvoiceSnapshotExport(finishExport);

    const exportSelectedRows = useCallback(
        async (format: ExportFormat) => {
            const selection = buildSelection({ scope: 'selection', period: undefined, selectedIds, viewIds: [] });
            if (selection === undefined) {
                return;
            }

            const outcome = await startExport({
                format,
                selection,
                documentCount: selectedIds.length,
                excelDefinitionId,
            });
            if (outcome === 'announced') {
                clearSelection();
            }
        },
        [selectedIds, startExport, excelDefinitionId, clearSelection],
    );

    const { openOnTheFlyModal, closeOnTheFlyModals } = useOnTheFlyModal();
    const navigate = useNavigate();
    const previewRows = useMemo(() => createPreviewRowsSource(), []);
    const onPreviewDocument = useCallback(
        (snapshotId: string) => {
            if (!loadedRows.some((row) => row.id === snapshotId)) {
                return;
            }

            let panelId = '';
            const close = () => closeOnTheFlyModals(panelId);
            const openDetail = (detailId: string) => {
                close();
                navigate(`../${detailId}`);
            };
            panelId = openOnTheFlyModal({
                label: 'Rechnungsvorschau',
                child: (
                    <InvoiceSnapshotQuickPreview
                        source={previewRows}
                        selectedIds={selectedIds}
                        startId={snapshotId}
                        onClose={close}
                        onOpenDetail={openDetail}
                    />
                ),
            });
        },
        [loadedRows, previewRows, selectedIds, openOnTheFlyModal, closeOnTheFlyModals, navigate],
    );

    const onLoaderDone = useCallback(
        ({ loadedItems, totalItems, fetchNextPage }: InvoiceSnapshotsLoaded) => {
            setLoadedRows(loadedItems);
            previewRows.set({ rows: loadedItems, total: totalItems, fetchNextPage });
        },
        [previewRows],
    );
    const loadedIds = useMemo(() => loadedRows.map((row) => row.id), [loadedRows]);

    const { download, isResolving } = useInvoiceSnapshotDocument();
    const onDownloadDocument = useCallback((snapshotId: string) => void download(snapshotId), [download]);

    const rowActionsColumn = useMemo(
        () =>
            canReadDocuments
                ? invoiceSnapshotRowActionsColumn({
                      onPreview: onPreviewDocument,
                      onDownload: onDownloadDocument,
                      locked: isResolving,
                  })
                : undefined,
        [canReadDocuments, onPreviewDocument, onDownloadDocument, isResolving],
    );

    const { tableModeValue, setTableMode, availableTableModes } = tableModeProps;
    const toolbarElements = useCallback(
        (elements: ToolbarOptions[]) => [
            ...elements,
            <SingleSelect<InvoiceSnapshotTableModes>
                key="tableMode"
                name="tableMode"
                items={availableTableModes}
                value={tableModeValue}
                onChange={setTableMode}
            />,
            <InvoiceSnapshotExportButton key="export" onClick={() => setIsExportOpen(true)} />,
        ],
        [availableTableModes, tableModeValue, setTableMode],
    );

    const viewProps = { objectSelector, onLoaderDone, toolbarElements, rowActionsColumn };

    return (
        <>
            {tableModeValue === 'opos' ? (
                <FrameListOpenInvoiceSnapshots {...viewProps} />
            ) : (
                <FrameListAllInvoiceSnapshots {...viewProps} />
            )}

            {isExportOpen && (
                <InvoiceSnapshotExportModal
                    onClose={closeExport}
                    selectedIds={selectedIds}
                    viewIds={loadedIds}
                    excelDefinitionId={excelDefinitionId}
                    canDownloadArchive={canReadDocuments}
                    startExport={startExport}
                    isStarting={isStarting}
                    isWaiting={isWaiting}
                    onExportStarted={clearSelection}
                />
            )}

            <FloatingToolbar isOpen={selectedIds.length > 0 && !isExportOpen}>
                <Badge size="sm" type="input" variant="primary">
                    {selectedIds.length.toString()}
                </Badge>
                Ausgewählt
                {canReadDocuments && (
                    <Button
                        appearance="filled"
                        variant="success"
                        onClick={() => void exportSelectedRows('zip')}
                        locked={isStarting || isWaiting}
                    >
                        <IconDownload size={defaultIconSize} />
                        &nbsp;{waitingFormat === 'zip' ? 'Wird erstellt …' : 'Als PDF-ZIP herunterladen'}
                    </Button>
                )}
                <Button
                    onClick={() => void exportSelectedRows('excel')}
                    locked={isStarting || isWaiting || excelDefinitionId === undefined}
                >
                    <IconTable size={defaultIconSize} />
                    &nbsp;{waitingFormat === 'excel' ? 'Wird erstellt …' : 'Excel-Export'}
                </Button>
                <Button appearance="transparent" onClick={clearSelection} label="Auswahl aufheben">
                    <IconX size={defaultIconSize} />
                </Button>
            </FloatingToolbar>
        </>
    );
}
